import { AlertCircleIcon } from '@prophecy/icons';
import { theme } from '@prophecy/theme';
import { Ellipsis } from '@prophecy/ui-v3/Ellipsis';
import { Stack } from '@prophecy/ui-v3/Layout';
import { Shimmer } from '@prophecy/ui-v3/Shimmer';
import { Text } from '@prophecy/ui-v3/Typography';
import { getBasePath } from '@prophecy/utils/history';
import { useLatestRef } from '@prophecy/utils/react/hooks';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled, { css } from 'styled-components';

import { hostUrl } from '../../utils/getServerUrl';
import { HOME_CHAT_TEST_IDS } from './constants';
import { WidgetFullscreenContext } from './widgetFullscreen';
import { createWidgetHost, inAppPath, resourceUriOf } from './widgetHost';

import type { HomeChatWidget, WidgetDisplayMode, WidgetToolResult } from './widgetHost';

export type { HomeChatWidget } from './widgetHost';

export type WidgetHostApi = {
  authorization: string | undefined;
  readResource: (uri: string) => Promise<{ html: string }>;
  callTool: (name: string, args: Record<string, unknown>) => Promise<unknown>;
};

const INITIAL_HEIGHT_PX = 320;
// allow-same-origin: an opaque-origin widget sends no Referer, and OSM then serves blocked map tiles.
// No allow-downloads: files come back through `ui/download-file`.
const IFRAME_SANDBOX = 'allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox allow-forms';

const Card = styled(Stack)<{ $fullscreen: boolean }>`
  position: relative;
  width: 100%;
  border-radius: ${theme.radius.x8};
  overflow: hidden;
  background: ${theme.colors.white};

  /* Inline, the widget draws its own border; fullscreen it drops it, so the card carries one. */
  ${({ $fullscreen }) =>
    $fullscreen &&
    css`
      border: 1px solid ${theme.colors.gray200};
      position: absolute;
      inset: ${theme.spaces.x16};
      width: auto;
      /* Above the page's fullscreen stage (s), which blanks the transcript around it. */
      z-index: ${theme.zLayer.m};
    `}
`;

const ErrorRow = styled(Stack)`
  width: 100%;
  min-width: 0;
  padding: ${theme.spaces.x8} ${theme.spaces.x12};
  border: 1px solid ${theme.colors.gray200};
  border-radius: ${theme.radius.x8};
  background: ${theme.colors.white};
`;

const LoadingOverlay = styled(Stack)`
  position: absolute;
  inset: 0;
  background: ${theme.colors.white};
`;

const Placeholder = styled(Stack)`
  width: 100%;
  border-radius: ${theme.radius.x8};
  background: ${theme.colors.gray100};
`;

const Frame = styled.iframe<{ $fullscreen: boolean }>`
  display: block;
  width: 100%;
  border: 0;
  ${({ $fullscreen }) => $fullscreen && 'height: 100% !important;'}
`;

function saveFile(fileName: string, mimeType: string, binary: string) {
  const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0));
  const url = URL.createObjectURL(new Blob([bytes], { type: mimeType || 'application/octet-stream' }));
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  link.click();
  URL.revokeObjectURL(url);
}

/** Hosts one prophecy-mcp widget the way Claude does: its HTML in a sandboxed iframe, JSON-RPC over postMessage. */
export function McpWidgetFrame({
  widget,
  host,
  onFollowUp
}: {
  widget: HomeChatWidget;
  host: WidgetHostApi;
  onFollowUp: (text: string) => Promise<unknown>;
}) {
  const frameRef = useRef<HTMLIFrameElement>(null);
  const [html, setHtml] = useState<string>();
  const [loadError, setLoadError] = useState<string>();
  const [height, setHeight] = useState(INITIAL_HEIGHT_PX);
  const [displayMode, setDisplayMode] = useState<WidgetDisplayMode>('inline');
  const [initialized, setInitialized] = useState(false);
  const navigate = useNavigate();
  const latest = useLatestRef({ widget, host, onFollowUp, navigate });
  const resourceUri = resourceUriOf(widget);
  // A second load of the same srcdoc means the frame navigated itself; the window stays the same
  // object, so from then on nothing is posted to it or accepted from it.
  const loadedHtml = useRef<string>();
  const leftWidget = useRef(false);
  const onFrameLoad = () => {
    if (loadedHtml.current === html) leftWidget.current = true;
    else loadedHtml.current = html;
  };

  useEffect(() => {
    if (!resourceUri) return setLoadError('This tool result has no view to show');
    let cancelled = false;
    setInitialized(false);
    latest.current.host
      .readResource(resourceUri)
      .then((resource) => !cancelled && setHtml(resource.html))
      .catch((error: unknown) => !cancelled && setLoadError(error instanceof Error ? error.message : String(error)));
    return () => {
      cancelled = true;
    };
  }, [resourceUri, latest]);

  const widgetHost = useMemo(
    () =>
      createWidgetHost({
        post: (message) =>
          !leftWidget.current && frameRef.current?.contentWindow?.postMessage(message, window.location.origin),
        toolResult: (): WidgetToolResult => ({
          structuredContent: latest.current.widget.structuredContent,
          _meta: latest.current.widget._meta
        }),
        authorization: () => latest.current.host.authorization,
        callTool: (name, args) => latest.current.host.callTool(name, args),
        sendFollowUp: (text) => latest.current.onFollowUp(text),
        openLink: (url) => {
          const path = inAppPath(url, {
            basePath: getBasePath(),
            origins: [window.location.origin, new URL(hostUrl).origin]
          });
          if (path) latest.current.navigate(path);
          else window.open(url, '_blank', 'noopener,noreferrer');
        },
        download: saveFile,
        setDisplayMode,
        setHeight,
        onInitialized: () => setInitialized(true)
      }),
    [latest]
  );

  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      if (!frameRef.current || event.source !== frameRef.current.contentWindow) return;
      if (event.origin !== window.location.origin || leftWidget.current) return;
      void widgetHost.handle(event.data);
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [widgetHost]);

  // The bearer can arrive after the handshake (a reopened chat renders before the broker is ready).
  useEffect(() => {
    if (html && host.authorization) widgetHost.deliverToolResult();
  }, [html, host.authorization, widgetHost]);

  const fullscreen = displayMode === 'fullscreen';
  const onFullscreenChange = useContext(WidgetFullscreenContext);
  useEffect(() => {
    if (!fullscreen) return;
    onFullscreenChange(true);
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      setDisplayMode('inline');
      widgetHost.notifyDisplayMode('inline');
    };
    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      onFullscreenChange(false);
    };
  }, [fullscreen, onFullscreenChange, widgetHost]);

  if (loadError) {
    return (
      <ErrorRow
        direction='horizontal'
        alignY='center'
        gap={theme.spaces.x8}
        data-test-id={HOME_CHAT_TEST_IDS.widgetError}>
        <AlertCircleIcon type='default' size='xs' color={theme.colors.error600} />
        <Text level='sm' weight={theme.fontWeight.medium} tone={theme.colors.gray700}>
          Couldn&apos;t load this view
        </Text>
        <Ellipsis tooltip>
          <Text level='sm' tone={theme.colors.gray500}>
            {loadError}
          </Text>
        </Ellipsis>
      </ErrorRow>
    );
  }
  // The widget's own API calls need the bearer, so it stays covered until both have arrived.
  const loading = !html || !host.authorization || !initialized;

  return (
    <Card $fullscreen={fullscreen} style={html ? undefined : { height: `${height}px` }}>
      {html && (
        <Frame
          ref={frameRef}
          title={widget.name ?? 'Prophecy view'}
          srcDoc={html}
          onLoad={onFrameLoad}
          sandbox={IFRAME_SANDBOX}
          allow='clipboard-write'
          style={{ height: `${height}px` }}
          $fullscreen={fullscreen}
          data-test-id={HOME_CHAT_TEST_IDS.widgetFrame}
        />
      )}
      {loading && (
        <LoadingOverlay data-test-id={HOME_CHAT_TEST_IDS.widgetLoading} aria-busy='true' aria-label='Loading view'>
          <Shimmer loading fullWidth>
            <Placeholder style={{ height: `${height}px` }} />
          </Shimmer>
        </LoadingOverlay>
      )}
    </Card>
  );
}
